import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import { useTranslation } from 'react-i18next';

const drawerWidth = 240;

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme } = useCustomTheme();
  const { t } = useTranslation();

  const allMenuItems = [
    { text: t('common.submitReport'), icon: <AssignmentIcon />, path: '/report/new' },
    { text: t('common.reports'), icon: <ListAltIcon />, path: '/reports' },
    ...(user?.isAdmin
      ? [
          { text: t('admin.users'), icon: <PeopleIcon />, path: '/admin/users' },
          { text: t('admin.reference'), icon: <FolderIcon />, path: '/reference' },
          { text: t('admin.adminPanel'), icon: <AdminPanelSettingsIcon />, path: '/admin' },
        ]
      : []),
    { text: t('common.profile'), icon: <PersonIcon />, path: '/profile' },
  ];

  // For admins: show all items in sidebar (desktop) or mobile menu
  // For non-admins: show in header buttons (desktop) or mobile menu
  const sidebarMenuItems = user?.isAdmin ? allMenuItems : [];
  const headerMenuItems = user?.isAdmin ? [] : allMenuItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {process.env.REACT_APP_ORG_NAME || t('common.appName')}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Menu items - show all for mobile, or sidebar items for admin desktop */}
        {(isMobile ? allMenuItems : sidebarMenuItems).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {user?.fullName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: user?.isAdmin ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: user?.isAdmin ? `${drawerWidth}px` : 0 },
        }}
      >
        <Toolbar>
          {/* Menu button - always visible on mobile, only for non-admins on desktop */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: user?.isAdmin ? 'none' : 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          {user?.isAdmin && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {process.env.REACT_APP_ORG_NAME || t('common.appName')}
          </Typography>
          {/* Header buttons for non-admins on desktop */}
          {!user?.isAdmin && !isMobile && (
            <>
              <Button
                color="inherit"
                startIcon={<AssignmentIcon />}
                onClick={() => navigate('/report/new')}
                sx={{ mr: 1 }}
              >
                {t('common.submitReport')}
              </Button>
              <Button
                color="inherit"
                startIcon={<ListAltIcon />}
                onClick={() => navigate('/reports')}
                sx={{ mr: 1 }}
              >
                {t('common.reports')}
              </Button>
              <Button
                color="inherit"
                startIcon={<PersonIcon />}
                onClick={() => navigate('/profile')}
                sx={{ mr: 1 }}
              >
                {t('common.profile')}
              </Button>
            </>
          )}
          <LanguageToggle />
          <ThemeToggle />
          <IconButton color="inherit" onClick={handleLogout}>
            <Typography variant="body2">{t('common.logout')}</Typography>
          </IconButton>
        </Toolbar>
      </AppBar>
      {/* Sidebar for admins (desktop) or mobile menu for everyone */}
      {(user?.isAdmin || isMobile) && (
        <Box
          component="nav"
          sx={{ width: { md: user?.isAdmin ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>
          {/* Permanent drawer only for admins on desktop */}
          {user?.isAdmin && (
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          )}
        </Box>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: user?.isAdmin ? `calc(100% - ${drawerWidth}px)` : '100%' },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;

